/**
 * AdminArchitectEdit — admin proxy-edit of an architect's public profile.
 *
 * Used for support cases (e.g. the architect can't access their portal,
 * needs an urgent correction, etc.). Mirrors the field set of
 * ProfileEditor but funnels through the admin endpoint, which:
 *   1. Requires a written reason (≥ 20 chars), persisted in audit log
 *   2. Sends an "edited by admin" notification + email to the architect
 *   3. Records `architect.profile_edited_by_admin` in activity_log
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getAdminArchitect, adminEditArchitectProfile } from "../lib/adminPortal";
import { uploadFile } from "../lib/applications";
import { Toaster, toast } from "sonner";
import BackBar from "../components/BackBar";
import { assetUrl } from "../lib/format";
import { ShieldAlert, Upload, X, Save, ArrowUpRight } from "lucide-react";
import { HighlightsEditor, CredentialsBlockEditor } from "./ProfileEditor";

export default function AdminArchitectEdit() {
  const { architectId } = useParams();
  const { user } = useRole();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id || !architectId) return;
    let alive = true;
    getAdminArchitect(architectId, user.id)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setDraft(toDraft(d));
      })
      .catch((e) => alive && setErr(e));
    return () => { alive = false; };
  }, [architectId, user?.id]);

  if (err) {
    return (
      <p style={{ color: "#A23B2E" }} data-testid="admin-arch-edit-error">
        Could not load this architect.
      </p>
    );
  }
  if (!data || !draft) {
    return (
      <div style={{ color: "#6C6B85", fontStyle: "italic" }} data-testid="admin-arch-edit-loading">
        Loading architect profile…
      </div>
    );
  }

  const reasonValid = reason.trim().length >= 20;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  async function onSave() {
    if (!reasonValid) {
      toast.error("Please describe why you're editing (at least 20 characters).");
      return;
    }
    setBusy(true);
    try {
      const fields = {
        firm_tagline: draft.firm_tagline,
        practice_statement: draft.practice_statement,
        bio: draft.bio,
        philosophy_quote: draft.philosophy_quote,
        photo: draft.photo,
        firm_photo: draft.firm_photo,
        years_founded:
          draft.years_founded === "" || draft.years_founded == null
            ? null
            : Number(draft.years_founded),
        team_size:
          draft.team_size === "" || draft.team_size == null
            ? null
            : Number(draft.team_size),
        years_practising:
          draft.years_practising === "" || draft.years_practising == null
            ? null
            : Number(draft.years_practising),
        lifestyle_focuses: (draft.lifestyle_focuses || [])
          .map((s) => String(s).trim())
          .filter(Boolean),
        // Pass 3 — admin-side override of architect-owned collections:
        credentials: draft.credentials,
        education: draft.education,
        awards: draft.awards,
        project_highlights: (draft.project_highlights || []).map((h) => ({
          project_name: (h.project_name || "").trim(),
          neighborhood: (h.neighborhood || "").trim(),
          lot_dimensions: (h.lot_dimensions || "").trim(),
          narrative: (h.narrative || "").trim(),
          completion_year:
            h.completion_year === "" || h.completion_year == null
              ? null
              : Number(h.completion_year),
          client_type: (h.client_type || "").trim(),
          built_from_design_id: h.built_from_design_id || "",
          built_from_design_name: h.built_from_design_name || "",
          image_ref: h.image_ref || "",
          metrics: (h.metrics || []).map((m) => ({
            label: (m.label || "").trim(),
            value: (m.value || "").trim(),
          })).filter((m) => m.label || m.value),
        })).filter((h) => h.project_name),
      };
      await adminEditArchitectProfile(architectId, user.id, reason.trim(), fields);
      toast.success("Profile updated and architect notified.");
      navigate(`/admin/architects/${architectId}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not save.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="animate-fade-in-up" data-testid="admin-arch-edit-page">
      <Toaster position="top-right" richColors closeButton />

      <BackBar
        parent={{
          to: `/admin/architects/${architectId}`,
          label: data.firm || data.name,
        }}
        trail={[
          { label: "Admin", to: "/admin" },
          { label: "Architects", to: "/admin/architects" },
          { label: data.firm || data.name, to: `/admin/architects/${architectId}` },
          { label: "Edit on their behalf" },
        ]}
      />

      <header className="mb-6">
        <div className="flex items-center gap-3">
          <span aria-hidden style={{ width: 28, height: 2, background: "#B14E2F" }} />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            <ShieldAlert size={11} style={{ display: "inline", marginRight: 6 }} />
            Admin proxy edit
          </span>
        </div>
        <h1
          className="mt-3"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: "clamp(28px, 4vw, 36px)",
            fontWeight: 500,
            color: "#1B1F4B",
            margin: 0,
            lineHeight: 1.15,
          }}
        >
          Edit {data.firm || data.name}.
        </h1>
        <p
          className="mt-2 max-w-[640px]"
          style={{ color: "#6C6B85", fontSize: 14, lineHeight: 1.6, margin: 0 }}
        >
          Use this only for support cases (architect can't log in, urgent
          correction, etc.). Every change is audit-logged and the architect is
          notified by email.
        </p>
      </header>

      {/* Reason — required */}
      <div
        className="wn-card"
        style={{
          padding: 18,
          borderLeft: "3px solid #B14E2F",
          backgroundColor: "rgba(177,78,47,0.04)",
        }}
        data-testid="admin-arch-edit-reason"
      >
        <label className="wn-label" htmlFor="admin-arch-edit-reason-input">
          Why are you editing on their behalf? (Required)
        </label>
        <textarea
          id="admin-arch-edit-reason-input"
          className="wn-input mt-2"
          style={{ minHeight: 80, resize: "vertical" }}
          placeholder="e.g. Architect cannot access portal — replacing typo in firm tagline at their phone request."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="admin-arch-edit-reason-input"
          maxLength={400}
        />
        <div
          className="mt-2 tabular-nums"
          style={{
            fontSize: 11.5,
            color: reasonValid ? "#4F6149" : "#8a651f",
          }}
        >
          {reason.trim().length}/20 characters minimum
        </div>
      </div>

      {/* Form */}
      <div className="mt-6 wn-card" style={{ padding: 22 }}>
        <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 20 }}>
          Profile fields.
        </h2>

        <div className="mt-5">
          <div className="wn-label">Headshot</div>
          <PhotoEditor
            value={draft.photo}
            onChange={(v) => set("photo", v)}
            testIdPrefix="admin-edit-photo"
          />
        </div>

        <div className="mt-5">
          <div className="wn-label">Firm hero photograph</div>
          <PhotoEditor
            value={draft.firm_photo}
            onChange={(v) => set("firm_photo", v)}
            testIdPrefix="admin-edit-firm-photo"
            aspect="4 / 3"
            width={132}
          />
        </div>

        <div className="mt-5 grid sm:grid-cols-3 gap-4">
          <Numeric
            label="Year firm founded"
            value={draft.years_founded}
            onChange={(v) => set("years_founded", v)}
            max={4}
            testid="admin-edit-years-founded"
          />
          <Numeric
            label="Team size"
            value={draft.team_size}
            onChange={(v) => set("team_size", v)}
            max={3}
            testid="admin-edit-team-size"
          />
          <Numeric
            label="Years practising"
            value={draft.years_practising}
            onChange={(v) => set("years_practising", v)}
            max={2}
            testid="admin-edit-years-practising"
          />
        </div>

        <div className="mt-5">
          <div className="wn-label">Firm tagline</div>
          <input
            className="wn-input mt-2"
            value={draft.firm_tagline}
            onChange={(e) => set("firm_tagline", e.target.value)}
            placeholder="A short sentence describing the practice's stance (5–10 words)."
            maxLength={140}
            data-testid="admin-edit-tagline"
          />
        </div>

        <div className="mt-5">
          <div className="wn-label">Philosophy quote</div>
          <input
            className="wn-input mt-2"
            value={draft.philosophy_quote}
            onChange={(e) => set("philosophy_quote", e.target.value)}
            placeholder="A single sentence pull-quote (≤ 160 chars)."
            maxLength={160}
            data-testid="admin-edit-philosophy"
          />
        </div>

        <div className="mt-5">
          <div className="wn-label">Practice statement</div>
          <textarea
            className="wn-input mt-2"
            value={draft.practice_statement}
            onChange={(e) => set("practice_statement", e.target.value)}
            style={{ minHeight: 100, resize: "vertical" }}
            data-testid="admin-edit-practice"
          />
        </div>

        <div className="mt-5">
          <div className="wn-label">Biography</div>
          <textarea
            className="wn-input mt-2"
            value={draft.bio}
            onChange={(e) => set("bio", e.target.value)}
            style={{ minHeight: 100, resize: "vertical" }}
            data-testid="admin-edit-bio"
          />
        </div>

        <div className="mt-5">
          <div className="wn-label">Lifestyle focuses (comma-separated)</div>
          <input
            className="wn-input mt-2"
            value={(draft.lifestyle_focuses || []).join(", ")}
            onChange={(e) =>
              set(
                "lifestyle_focuses",
                e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .slice(0, 4),
              )
            }
            placeholder="Multi-generational households, First-time buyers, …"
            data-testid="admin-edit-lifestyle"
          />
          <div style={{ color: "#6C6B85", fontSize: 11, marginTop: 4 }} className="tabular-nums">
            {(draft.lifestyle_focuses || []).length}/4 entries
          </div>
        </div>
      </div>

      {/* Pass-3 admin override: credentials / education / awards */}
      <div className="mt-6 wn-card" style={{ padding: 22 }}>
        <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 20 }}>
          Credentials & recognition.
        </h2>
        <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 6, lineHeight: 1.5, maxWidth: 560 }}>
          These collections normally belong to the architect. Use this section
          only when correcting an error on the architect's behalf (e.g.
          updating a misspelt institution).
        </p>
        <div className="mt-4">
          <CredentialsBlockEditor
            label="Credentials"
            rows={draft.credentials || []}
            schema={[
              { key: "abbr", placeholder: "AIA", grow: 1 },
              { key: "year", placeholder: "Year", grow: 1, type: "number", tabular: true },
              { key: "name", placeholder: "Full credential name", grow: 4 },
            ]}
            onChange={(arr) => set("credentials", arr)}
            emptyTemplate={{ abbr: "", year: "", name: "" }}
            testid="admin-credentials"
          />
          <CredentialsBlockEditor
            label="Education"
            rows={draft.education || []}
            schema={[
              { key: "degree", placeholder: "M.Arch", grow: 2 },
              { key: "year", placeholder: "Year", grow: 1, type: "number", tabular: true },
              { key: "institution", placeholder: "Institution", grow: 4 },
            ]}
            onChange={(arr) => set("education", arr)}
            emptyTemplate={{ degree: "", year: "", institution: "" }}
            testid="admin-education"
          />
          <CredentialsBlockEditor
            label="Recognition (awards)"
            rows={draft.awards || []}
            schema={[
              { key: "name", placeholder: "Award name", grow: 3 },
              { key: "year", placeholder: "Year", grow: 1, type: "number", tabular: true },
              { key: "body", placeholder: "Awarding body", grow: 3 },
              { key: "description", placeholder: "Short description", grow: 6, multiline: true },
            ]}
            onChange={(arr) => set("awards", arr)}
            emptyTemplate={{ name: "", year: "", body: "", description: "" }}
            testid="admin-awards"
          />
        </div>
      </div>

      {/* Pass-3 admin override: project highlights */}
      <div className="mt-6 wn-card" style={{ padding: 22 }} data-testid="admin-highlights-section">
        <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 20 }}>
          Completed-project highlights.
        </h2>
        <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 6, lineHeight: 1.5, maxWidth: 560 }}>
          Edit the case studies that appear in the "Completed projects" rail
          of the architect's public profile.
        </p>
        <HighlightsEditor
          highlights={draft.project_highlights || []}
          onChange={(arr) => set("project_highlights", arr)}
          designs={data.designs_list || []}
        />
      </div>

      {/* Action bar */}
      <div
        className="mt-7 flex items-center justify-end flex-wrap"
        style={{ gap: 12 }}
        data-testid="admin-arch-edit-actions"
      >
        <Link
          to={`/architects/${architectId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="wn-btn wn-btn-tertiary inline-flex items-center"
          style={{ gap: 6 }}
          data-testid="admin-arch-edit-preview"
        >
          Preview live profile <ArrowUpRight size={14} />
        </Link>
        <Link
          to={`/admin/architects/${architectId}`}
          className="wn-btn wn-btn-outline"
          data-testid="admin-arch-edit-cancel"
        >
          Cancel
        </Link>
        <button
          type="button"
          className="wn-btn wn-btn-primary inline-flex items-center"
          style={{ gap: 6 }}
          onClick={onSave}
          disabled={!reasonValid || busy}
          data-testid="admin-arch-edit-save"
        >
          <Save size={14} />
          {busy ? "Saving…" : "Save & notify architect"}
        </button>
      </div>
    </section>
  );
}

/* ───────── helpers ───────── */
function toDraft(p) {
  return {
    photo: p.photo || "",
    firm_photo: p.firm_photo || "",
    firm_tagline: p.firm_tagline || "",
    philosophy_quote: p.philosophy_quote || "",
    practice_statement: p.practice_statement || "",
    bio: p.bio || "",
    years_founded: p.years_founded == null ? "" : String(p.years_founded),
    team_size: p.team_size == null ? "" : String(p.team_size),
    years_practising: p.years_practising == null ? "" : String(p.years_practising),
    lifestyle_focuses: (p.lifestyle_focuses || []).slice(),
    credentials: (p.credentials || []).map((c) => ({ ...c })),
    education: (p.education || []).map((e) => ({ ...e })),
    awards: (p.awards || []).map((a) => ({ ...a })),
    project_highlights: (p.project_highlights || []).map((h) => ({ ...h, _open: false })),
  };
}

function Numeric({ label, value, onChange, max, testid }) {
  return (
    <div>
      <div className="wn-label">{label}</div>
      <input
        className="wn-input mt-2 tabular-nums"
        inputMode="numeric"
        value={value || ""}
        onChange={(e) =>
          onChange(e.target.value.replace(/[^\d]/g, "").slice(0, max))
        }
        data-testid={testid}
      />
    </div>
  );
}

function PhotoEditor({ value, onChange, testIdPrefix = "admin-photo", aspect, width = 88 }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  async function onPick(file) {
    if (!file) return;
    setBusy(true);
    try {
      const meta = await uploadFile(file, "profile-photo");
      onChange(meta.url);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed.";
      toast.error(detail);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }
  const style = aspect
    ? {
        width,
        aspectRatio: aspect,
        borderRadius: 4,
        background: value
          ? `url(${assetUrl(value)}) center/cover no-repeat`
          : "#EEEDF3",
        border: "1px solid #D9D8E4",
        flexShrink: 0,
      }
    : {
        width,
        height: width,
        borderRadius: 10,
        background: value
          ? `url(${assetUrl(value)}) center/cover no-repeat`
          : "#EEEDF3",
        border: "1px solid #D9D8E4",
        flexShrink: 0,
      };
  return (
    <div className="mt-2 flex items-center gap-3">
      <div style={style} data-testid={`${testIdPrefix}-preview`} />
      <div>
        <button
          type="button"
          className="wn-btn wn-btn-outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          <Upload size={14} /> {busy ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
        </button>
        {value && (
          <button
            type="button"
            className="wn-btn wn-btn-tertiary ml-2"
            onClick={() => onChange("")}
            data-testid={`${testIdPrefix}-clear-btn`}
            style={{ color: "#A23B2E" }}
          >
            <X size={12} /> Remove
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          onChange={(e) => onPick(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}
